import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import uniqueId from 'transmute/uniqueId';
import { Popover, PopoverBody } from 'foundations-components/transitional/components/Popover';
import { Flex } from 'foundations-components/components/Flex';
import { Select } from 'foundations-components/transitional/components/Select';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Link } from 'foundations-components/transitional/components/Link';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import ReferenceLiteName from 'reference-resolvers-lite/components/ReferenceLiteName';
import { CRM_OBJECT } from 'customer-data-objects/constants/CrmObjectType';
import UniversalAssociationsSelectError from './UniversalAssociationsSelectError';
import UniversalAssociationsSelectSearchPane, { SearchPaneSkeleton } from './UniversalAssociationsSelectSearchPane';
import { getRefreshUrl } from '../helpers/getRefreshUrl';
import UniversalAssociationsSelectSideNav from './UniversalAssociationsSelectSideNav';
import { UniversalAssociationsSelectSideNavSkeleton } from './UniversalAssociationsSelectSideNavSkeleton';
import { ShinySkeletonText } from './ShinySkeletonText';
import useAutoDisassociateOneToOneAssociation from '../hooks/useAutoDisassociateOneToOneAssociation';
import { Metrics } from '../helpers/Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_HEIGHT = 260;
const POPOVER_WITH_SIDENAV_WIDTH = 480;
const POPOVER_WITHOUT_SIDENAV_WIDTH = 320;
const LG_POPOVER_HEIGHT = 460;
const LG_POPOVER_WITH_SIDENAV_WIDTH = 680;
const LG_POPOVER_WITHOUT_SIDENAV_WIDTH = 520;
const associationCountReducer = (acc, association) => {
  const isSelectedCount = association.get('associationOptionRecords').filter(associationOptionRecord => associationOptionRecord.get('isSelected')).size;
  return acc + isSelectedCount;
};
function UniversalAssociationsSelect({
  'aria-labelledby': ariaLabelledBy,
  apiClient,
  associationRecords,
  betAssociationsCount = 0,
  betSelect = null,
  buttonUse = 'link',
  canEdit = false,
  canView = true,
  currentUserCanCommunicateWithEngagement,
  descriptionLabelKey,
  emailRecipientsIds,
  engagementOrInteractionType,
  isError = false,
  isLoading = false,
  labelKeyWhenNotEditable = 'universalAssociationsSelect.selectedAssociationsLabel',
  onFocus,
  onSelectAssociation,
  onUnselectAssociation,
  onPopoverOpenChange,
  placement = 'bottom right',
  popoverLabelKey = 'universalAssociationsSelect.button',
  popoverLabel,
  toolTipTitle,
  preselectedObjectTypeId,
  readOnly,
  shouldDisableLastAssociation = true,
  displayAllAssociations,
  singleObjectSelectObjectTypeId,
  skeletonStateWidth = 175,
  size = 'small',
  summaryAssociations,
  summaryAssociationsCount,
  OptionWrapperComponent,
  FooterComponent,
  sideNavInlineSize
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShowPopoverOpenMessage, setShouldShowPopoverOpenMessage] = useState(false);
  const [selectedObjectTypeId, setSelectedObjectTypeId] = useState(preselectedObjectTypeId || singleObjectSelectObjectTypeId);
  const selectUniqueId = useRef('');
  const shouldShowSideNav = !singleObjectSelectObjectTypeId;
  useEffect(() => {
    selectUniqueId.current = uniqueId('universal-associations-select-dropdown-');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For ONE_TO_ONE associations, we automatically unselect the existing selected association.
  const setAutoDisassociateRecord = useAutoDisassociateOneToOneAssociation({
    associationRecords,
    onUnselectAssociation
  });
  const handleToggleIsOpen = useCallback(() => {
    setIsOpen(!isOpen);
    onPopoverOpenChange === null || onPopoverOpenChange === void 0 || onPopoverOpenChange(!isOpen);
  }, [isOpen, onPopoverOpenChange]);
  const handleClosePopover = useCallback(() => {
    setIsOpen(false);
    onPopoverOpenChange === null || onPopoverOpenChange === void 0 || onPopoverOpenChange(false);
  }, [onPopoverOpenChange]);
  const handleSetSelectedObjectTypeId = useCallback(objectType => {
    setSelectedObjectTypeId(objectType);
  }, [setSelectedObjectTypeId]);
  const allAssociationsCount = useMemo(() => {
    if (isLoading || isError) {
      return 0;
    }
    if (associationRecords.size) {
      return associationRecords.reduce(associationCountReducer, 0);
    }
    return summaryAssociationsCount || 0;
  }, [associationRecords, isLoading, isError, summaryAssociationsCount]);
  const singleObjectTypeIdAssociationCount = useMemo(() => {
    if (isLoading || isError || !singleObjectSelectObjectTypeId) {
      return 0;
    }
    if (associationRecords.size) {
      const filteredAssociations = associationRecords.filter(record => record.get('toObjectTypeId') === singleObjectSelectObjectTypeId);
      return filteredAssociations.reduce(associationCountReducer, 0);
    }
    const summarySingleObjectCount = summaryAssociations && summaryAssociations[singleObjectSelectObjectTypeId] && summaryAssociations[singleObjectSelectObjectTypeId].length;
    return summarySingleObjectCount || 0;
  }, [isLoading, isError, singleObjectSelectObjectTypeId, associationRecords, summaryAssociations]);
  const handleToggleSelect = useCallback((associationRecord, associationOptionRecord) => {
    if (associationOptionRecord.get('isSelected')) {
      return onUnselectAssociation(associationRecord, associationOptionRecord);
    }
    const existingSelectedAssociationOptionRecord = associationRecord.get('associationOptionRecords').find(record => record.get('isSelected'));

    // If 1:1, find and prepare to unselect any existing selected association.
    if (associationRecord.get('cardinality') === 'ONE_TO_ONE' && existingSelectedAssociationOptionRecord) {
      setAutoDisassociateRecord({
        association: associationRecord,
        associationOption: existingSelectedAssociationOptionRecord
      });
    }
    return onSelectAssociation(associationRecord, associationOptionRecord);
  }, [onSelectAssociation, onUnselectAssociation, setAutoDisassociateRecord]);
  const getShouldDisableLastAssociation = useCallback(() => {
    if (!shouldDisableLastAssociation) {
      return false;
    }
    if (!shouldShowSideNav) {
      return singleObjectTypeIdAssociationCount === 1 && allAssociationsCount === 1;
    }
    return allAssociationsCount === 1;
  }, [allAssociationsCount, singleObjectTypeIdAssociationCount, shouldDisableLastAssociation, shouldShowSideNav]);
  const PopoverContent = useCallback(() => {
    const popoverHeight = size === 'large' ? LG_POPOVER_HEIGHT : POPOVER_HEIGHT;
    const renderedPopoverItems = associationRecords.size ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [shouldShowSideNav && /*#__PURE__*/_jsx(UniversalAssociationsSelectSideNav, {
        associationRecords: associationRecords,
        allAssociationsCount: allAssociationsCount,
        selectedObjectTypeId: selectedObjectTypeId,
        onSetSelectedObjectTypeId: handleSetSelectedObjectTypeId,
        inlineSize: sideNavInlineSize
      }), /*#__PURE__*/_jsx(UniversalAssociationsSelectSearchPane, {
        associationRecords: associationRecords,
        canEdit: canEdit,
        emailRecipientsIds: emailRecipientsIds,
        engagementOrInteractionType: engagementOrInteractionType,
        currentUserCanCommunicateWithEngagement: currentUserCanCommunicateWithEngagement,
        onToggleSelectAssociation: handleToggleSelect,
        readOnly: readOnly,
        selectedObjectTypeId: selectedObjectTypeId,
        shouldDisableLastAssociation: getShouldDisableLastAssociation(),
        shouldAddSearchBackground: !shouldShowSideNav,
        shouldShowPopoverOpenMessage: shouldShowPopoverOpenMessage,
        setShouldShowPopoverOpenMessage: setShouldShowPopoverOpenMessage,
        OptionWrapperComponent: OptionWrapperComponent,
        displayAllAssociations: displayAllAssociations,
        apiClient: apiClient
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [shouldShowSideNav && /*#__PURE__*/_jsx(UniversalAssociationsSelectSideNavSkeleton, {}), /*#__PURE__*/_jsx(SearchPaneSkeleton, {
        isSingleObjectType: !shouldShowSideNav
      })]
    });
    return /*#__PURE__*/_jsxs(ErrorBoundary, {
      boundaryName: "AssociationsSelectError",
      ErrorComponent: UniversalAssociationsSelectError,
      children: [/*#__PURE__*/_jsx(PopoverBody, {
        padding: "0",
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "row",
          blockSize: `${popoverHeight}px`,
          children: renderedPopoverItems
        })
      }), FooterComponent && /*#__PURE__*/_jsx(FooterComponent, {
        closePopover: handleClosePopover
      }), betSelect]
    });
  }, [shouldShowSideNav, sideNavInlineSize, associationRecords, allAssociationsCount, handleSetSelectedObjectTypeId, selectedObjectTypeId, canEdit, emailRecipientsIds, engagementOrInteractionType, currentUserCanCommunicateWithEngagement, handleToggleSelect, readOnly, getShouldDisableLastAssociation, betSelect, size, shouldShowPopoverOpenMessage, displayAllAssociations, OptionWrapperComponent, FooterComponent, handleClosePopover, apiClient]);
  const objectPrimaryDisplayLabel = useMemo(() => {
    // singleObjectTypeIdAssociationCount is only calculated when singleObjectSelectObjectTypeId has a value
    if (singleObjectTypeIdAssociationCount !== 1) {
      return null;
    }
    if (associationRecords.size) {
      const object = associationRecords.getIn([selectedObjectTypeId, 'associationOptionRecords']).find(record => record.get('isSelected'));
      return object.get('primaryDisplayLabel');
    }
    const summaryObjectId = summaryAssociations && selectedObjectTypeId && summaryAssociations[selectedObjectTypeId] && summaryAssociations[selectedObjectTypeId][0].id;
    return summaryObjectId ? /*#__PURE__*/_jsx(ReferenceLiteName, {
      id: `${summaryObjectId}`,
      objectTypeId: selectedObjectTypeId,
      referenceType: CRM_OBJECT
    }) : null;
  }, [associationRecords, selectedObjectTypeId, singleObjectTypeIdAssociationCount, summaryAssociations]);
  const shouldDisableErrorToolTip = canView && !isError;
  const PopoverLabel = useMemo(() => {
    if (!canView) {
      // Currently users with ownedOnly permissions cannot view associations of an engagement they do not own through
      // GraphQL. Rather than show an error, we render a placeholder that is disabled. However, currently this is not supported
      // in permissions. https://git.hubteam.com/HubSpot/CRM-Issues/issues/7309
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: labelKeyWhenNotEditable
      });
    }
    if (isError) {
      Metrics.counter('universal-associations-select-error').increment();
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.dataFetchError.button"
      });
    }
    const count = shouldShowSideNav ? allAssociationsCount + betAssociationsCount : singleObjectTypeIdAssociationCount;
    return /*#__PURE__*/_jsx(TruncateString, {
      "data-test-id": 'universal-associations-select-button-placeholder',
      maxWidth: 200,
      useFlex: true,
      children: objectPrimaryDisplayLabel || popoverLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: popoverLabelKey,
        options: {
          count
        }
      })
    });
  }, [canView, isError, shouldShowSideNav, allAssociationsCount, betAssociationsCount, singleObjectTypeIdAssociationCount, objectPrimaryDisplayLabel, popoverLabel, popoverLabelKey, labelKeyWhenNotEditable]);
  const TooltipText = useMemo(() => !canView ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "universalAssociationsSelect.dataFetchError.tooltipPermissionErrorV2"
  }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "universalAssociationsSelect.dataFetchError.tooltip_jsx",
    options: {
      href: getRefreshUrl()
    },
    elements: {
      Link
    }
  }), [canView]);
  const headingText = useMemo(() => !canView ? undefined : unescapedText('universalAssociationsSelect.dataFetchError.tooltipHeading'), [canView]);
  const TooltipWrapper = useMemo(() => ({
    children
  }) => toolTipTitle ? /*#__PURE__*/_jsx(Tooltip, {
    title: toolTipTitle,
    children: children
  }) : /*#__PURE__*/_jsx(Tooltip, {
    disabled: shouldDisableErrorToolTip,
    headingText: headingText,
    title: TooltipText,
    children: children
  }), [shouldDisableErrorToolTip, headingText, TooltipText, toolTipTitle]);
  const width = useMemo(() => {
    if (size === 'large') {
      return shouldShowSideNav ? LG_POPOVER_WITH_SIDENAV_WIDTH : LG_POPOVER_WITHOUT_SIDENAV_WIDTH;
    }
    return shouldShowSideNav ? POPOVER_WITH_SIDENAV_WIDTH : POPOVER_WITHOUT_SIDENAV_WIDTH;
  }, [shouldShowSideNav, size]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(ShinySkeletonText, {
      "data-test-id": "universal-associations-select-loading",
      width: skeletonStateWidth
    });
  }
  const descriptionUniqueId = uniqueId('universal-associations-select-button-');
  return /*#__PURE__*/_jsx(Popover, {
    "data-selenium-test": "universal-associations-select-popover",
    content: PopoverContent(),
    closeOnOutsideClick: true,
    onOpenChange: handleToggleIsOpen,
    open: isOpen,
    onOpenStart: () => setShouldShowPopoverOpenMessage(true),
    placement: placement,
    inlineSize: `${width}px`,
    overflow: "hidden",
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(TooltipWrapper, {
        children: /*#__PURE__*/_jsx(Select, {
          "aria-describedby": descriptionUniqueId,
          "aria-labelledby": ariaLabelledBy
          // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
          ,
          onClick: handleToggleIsOpen,
          onFocus: onFocus,
          onMouseEnter: onFocus,
          open: false,
          placeholder: PopoverLabel,
          buttonUse: buttonUse,
          "aria-pressed": isOpen,
          "data-test-id": "universal-associations-select-button",
          "data-selenium-test": "universal-associations-select-button",
          disabled: !shouldDisableErrorToolTip,
          id: selectUniqueId.current
        })
      }), /*#__PURE__*/_jsx(ScreenReaderOnly, {
        children: /*#__PURE__*/_jsx("div", {
          id: descriptionUniqueId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: descriptionLabelKey || 'universalAssociationsSelect.accessibility.button'
          })
        })
      })]
    })
  });
}
export default UniversalAssociationsSelect;